/**
 * v0.10.3 BPMN lane-plan lints for the LSP (plan 2026-07-07-bpmn-lanes-pools
 * §7 — "semantic lints live in the LSP view-validation pass").
 *
 * `resolveBpmnLanes` already computes the semantic lints (a lane matching no
 * participant, a multi-value aspect placed in the first lane, a pools-only
 * body, an unevaluable selector) as it builds the plan; the render path just
 * drops them. This pass walks every declared `flow … bpmn { … }` view
 * fresh-open and surfaces those lints as diagnostics anchored at the offending
 * clause — the empty-lane and multi-value ones at their own clause, the
 * body-shape `pools-only` lint at the `flow` clause (it has no clause of its
 * own). The result is merged into the package's diagnostic stream in
 * `Workspace.recomputeFromState`, same as policy findings.
 */

import {
  type BpmnLaneLint,
  type Diagnostic,
  type DiagnosticCode,
  type DiagnosticSeverity,
  type ParsedFile,
  type QueryDecl,
  type ResolvedModel,
} from "@archlang/engine";
import { computeBpmnViewLints } from "@archlang/viewer-core";

/** Lane lints are advisory — no lint blocks a render, so none is an `error`. */
const SEVERITY: Record<BpmnLaneLint["kind"], DiagnosticSeverity> = {
  "empty-lane": "warning",
  "bad-selector": "warning",
  "pools-only": "warning",
  "multi-value": "info",
};

/** Stable per-kind codes (for future `severity:` config remapping). */
const CODE: Record<BpmnLaneLint["kind"], DiagnosticCode> = {
  "empty-lane": "BPMN_LANE_EMPTY",
  "bad-selector": "BPMN_LANE_BAD_SELECTOR",
  "pools-only": "BPMN_POOLS_ONLY",
  "multi-value": "BPMN_LANE_MULTI_VALUE",
};

/**
 * Diagnostics for the lane plans of a package's `flow … bpmn` views. Named
 * queries reachable from a lane/pool selector come from the package's parsed
 * files (the query closure the evaluator needs); an unresolved one reads as an
 * empty ref, exactly like the headless render path.
 */
export function computeViewLintDiagnostics(
  cascaded: ResolvedModel,
  files: Iterable<ParsedFile>,
): Diagnostic[] {
  const queries = new Map<string, QueryDecl>();
  for (const f of files) {
    for (const d of f.declarations) {
      if (d.kind === "query") queries.set(d.decl.name, d.decl);
    }
  }
  const out: Diagnostic[] = [];
  for (const { lint, span } of computeBpmnViewLints(cascaded, queries)) {
    if (!span) continue; // unanchorable — nothing to attach a squiggle to
    out.push({
      severity: SEVERITY[lint.kind],
      message: lint.message,
      source: span,
      code: CODE[lint.kind],
    });
  }
  return out;
}
